import React, { useCallback, useEffect, useState } from 'react';
import { isInApp } from '../../utils/isInApp';
import AppPaymentBlocker from '../../components/AppPaymentBlocker';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { trackSchoolPurchaseEvent } from '../../utils/eventTracker';
import './BulkPurchasePage.css';

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const loadTossSdk = () => new Promise((resolve, reject) => {
  if (window.TossPayments) return resolve();
  const script = document.createElement('script');
  script.src = 'https://js.tosspayments.com/v1/payment';
  script.async = true;
  script.onload = () => resolve();
  script.onerror = () => reject(new Error('토스 페이먼츠 SDK 로드 실패'));
  document.head.appendChild(script);
});

const won = (value) => `₩${Number(value || 0).toLocaleString()}`;

const formatKstDate = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const kst = new Date(date.getTime() + 9 * 60 * 60 * 1000);
  return `${kst.getUTCFullYear()}년 ${kst.getUTCMonth() + 1}월 ${kst.getUTCDate()}일`;
};

const CLOSED_MESSAGES = {
  paid: { title: '이미 결제가 완료됐어요', body: '쿠폰과 카드 매출전표는 결제하신 메일로 발송됐어요. 메일을 받지 못하셨다면 02-949-9158로 연락해 주세요.' },
  expired: { title: '결제 링크가 만료됐어요', body: '담당자에게 새 결제 링크를 요청해 주세요. 02-949-9158' },
  revoked: { title: '더 이상 쓸 수 없는 링크예요', body: '조건이 바뀌어 새 링크가 발송됐을 수 있어요. 가장 최근 메일의 링크를 이용하시거나 담당자에게 문의해 주세요.' },
};

const BulkPaymentLinkCheckout = ({ token }) => {
  const [link, setLink] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [tossReady, setTossReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/api/bulk-purchase/payment-link/${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || '결제 링크를 불러오지 못했어요.');
        return data;
      })
      .then((data) => {
        if (cancelled) return;
        setLink(data);
        setContactName(data.contact_name || '');
        setEmail(data.email || '');
      })
      .catch((err) => { if (!cancelled) setLoadError(err.message); });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    loadTossSdk().then(() => setTossReady(true)).catch(() => {
      setErrorMsg('결제 시스템을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.');
    });
  }, []);

  const validate = useCallback(() => {
    if (!contactName.trim()) return '결제 담당자 이름을 입력해 주세요.';
    if (!EMAIL_RE.test(email.trim())) return '쿠폰을 받을 이메일 주소를 정확히 입력해 주세요.';
    const digits = phone.replace(/[^0-9]/g, '');
    if (digits && (digits.length < 10 || digits.length > 11)) return '휴대폰 번호를 정확히 입력하거나 비워 주세요.';
    if (!agreed) return '결제 안내사항에 동의해 주세요.';
    if (!tossReady) return '결제 시스템 준비 중이에요. 잠시 후 다시 시도해 주세요.';
    return null;
  }, [contactName, email, phone, agreed, tossReady]);

  const handleSubmit = async () => {
    setErrorMsg('');
    const err = validate();
    if (err) {
      setErrorMsg(err);
      return;
    }
    setIsProcessing(true);
    const cleanEmail = email.trim().toLowerCase();
    const digits = phone.replace(/[^0-9]/g, '');
    const checkoutMeta = { entry: 'payment_link', path: 'card', plan: 'custom', quantity: link.quantity };
    trackSchoolPurchaseEvent('card_checkout_started', checkoutMeta);
    let failReason = 'exception';
    try {
      const orderRes = await fetch(`${getApiBaseUrl()}/api/bulk-purchase/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_link: token,
          school_name: link.school_name,
          plan_id: 'custom',
          quantity: link.quantity,
          apply_promo: false,
          contact_name: contactName.trim(),
          email: cleanEmail,
          phone: digits,
          landing_page: window.location.pathname,
          referrer: document.referrer || null,
        }),
      });
      const orderData = await orderRes.json().catch(() => ({}));
      if (!orderRes.ok) {
        failReason = `http_${orderRes.status}`;
        throw new Error(orderData.detail || '주문을 만들지 못했어요.');
      }

      failReason = 'toss';
      const tossPayments = window.TossPayments(orderData.toss_config.client_key);
      const orderId = encodeURIComponent(orderData.order_id);
      await tossPayments.requestPayment('카드', {
        amount: orderData.amount,
        orderId: orderData.order_id,
        orderName: orderData.order_name,
        successUrl: `${window.location.origin}/bulk-purchase/success?orderId=${orderId}`,
        failUrl: `${window.location.origin}/bulk-purchase/fail?orderId=${orderId}`,
        customerEmail: cleanEmail,
        customerName: contactName.trim(),
        ...(digits ? { customerMobilePhone: digits } : {}),
      });
    } catch (e) {
      setIsProcessing(false);
      const msg = e?.message || '결제 요청 중 오류가 발생했어요.';
      if (e?.code === 'USER_CANCEL' || /USER_CANCEL/i.test(msg)) {
        trackSchoolPurchaseEvent('card_checkout_cancelled', checkoutMeta);
        setErrorMsg('결제가 취소됐어요. 다시 시도해 주세요.');
      } else {
        const reason = failReason === 'toss' && typeof e?.code === 'string' ? e.code : failReason;
        trackSchoolPurchaseEvent('card_checkout_failed', { ...checkoutMeta, reason });
        setErrorMsg(msg);
      }
    }
  };

  if (isInApp()) {
    return (
      <AppPaymentBlocker
        pageName="카드 결제"
        webUrl={`https://whatquiz.co.kr${window.location.pathname}${window.location.search}`}
        fallbackPath="/pricing"
      />
    );
  }

  const closed = link && CLOSED_MESSAGES[link.status];
  const supply = link ? Math.round(link.total_amount / 1.1) : 0;
  const vat = link ? link.total_amount - supply : 0;

  return (
    <div className="bulk-page">
      <div className="bulk-container">
        <header className="bulk-header">
          <div>
            <h1 className="bulk-title">{link?.school_name ? `${link.school_name} 카드 결제` : '카드 결제'}</h1>
            <p className="bulk-subtitle">협의한 조건 그대로 결제돼요 · 결제 즉시 쿠폰과 매출전표 발송</p>
          </div>
        </header>

        {!link && !loadError && <section className="bulk-section"><span className="bulk-spinner is-quote" /> 결제 정보를 불러오고 있어요</section>}

        {loadError && (
          <section className="bulk-section">
            <h2 className="bulk-section-title">결제 링크를 열 수 없어요</h2>
            <p className="bulk-section-desc">{loadError}</p>
          </section>
        )}

        {closed && (
          <section className="bulk-section">
            <h2 className="bulk-section-title">{closed.title}</h2>
            <p className="bulk-section-desc">{closed.body}</p>
          </section>
        )}

        {link && link.status === 'active' && (
          <>
            <section className="bulk-section bulk-summary">
              <h2 className="bulk-section-title"><i>1</i>결제 내용</h2>
              <table className="bulk-summary-table">
                <tbody>
                  <tr><td>학교/기관</td><td>{link.school_name}</td></tr>
                  <tr><td>상품</td><td>왓퀴즈AI PRO {link.duration_months}개월권 <span className="bulk-summary-sub">(쿠폰 등록일부터 {link.plan_days}일)</span></td></tr>
                  <tr><td>수량</td><td>{link.quantity}개</td></tr>
                  <tr><td>공급가액</td><td>{won(supply)}</td></tr>
                  <tr><td>부가세 (10%)</td><td>{won(vat)}</td></tr>
                  <tr className="bulk-summary-total"><td>총 결제 금액</td><td>{won(link.total_amount)}</td></tr>
                </tbody>
              </table>
              {link.expires_at && <p className="bulk-field-hint">이 링크는 {formatKstDate(link.expires_at)}까지 쓸 수 있어요.</p>}
            </section>

            <section className="bulk-section">
              <h2 className="bulk-section-title"><i>2</i>결제자 정보</h2>
              <p className="bulk-section-desc">쿠폰 {link.quantity}개와 카드 매출전표를 이 이메일로 보내드려요.</p>
              <div className="bulk-row">
                <div className="bulk-field">
                  <label htmlFor="paylink-name">담당자 이름 *</label>
                  <input id="paylink-name" type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} maxLength={40} />
                </div>
                <div className="bulk-field">
                  <label htmlFor="paylink-phone">휴대폰 번호 <span className="bulk-opt">(선택, 쿠폰 문자 발송)</span></label>
                  <input id="paylink-phone" type="tel" placeholder="01012345678" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={20} />
                </div>
              </div>
              <div className="bulk-field">
                <label htmlFor="paylink-email">이메일 *</label>
                <input id="paylink-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={120} />
              </div>

              <div className="bulk-notice">
                <p className="bulk-notice-title">결제 전 꼭 확인해 주세요</p>
                <ul>
                  <li>결제하면 <strong>쿠폰 {link.quantity}개</strong>가 발급돼요. 결제자 계정에 PRO가 자동 적용되지는 않아요.</li>
                  <li>선생님 한 분당 쿠폰 1개씩 등록하시면 돼요.</li>
                  <li>카드 매출전표는 메일로 자동 발송돼요. 세금계산서가 필요하시면 메일에 회신해 주세요.</li>
                  <li>환불은 <a href="mailto:angel@plactical.com">angel@plactical.com</a>으로 문의해 주세요. 이미 등록된 쿠폰은 환불되지 않아요.</li>
                </ul>
              </div>

              <label className="bulk-agree">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                <span>위 안내사항을 확인했고, 카드 결제에 동의해요.</span>
              </label>

              {errorMsg && <div className="bulk-error" role="alert">{errorMsg}</div>}

              <button className="bulk-submit" onClick={handleSubmit} disabled={isProcessing || !tossReady}>
                {isProcessing ? <span className="bulk-spinner" /> : `${won(link.total_amount)} 카드로 결제하기`}
              </button>
              <p className="bulk-footnote">토스페이먼츠 안전결제로 처리돼요 · 문의 02-949-9158</p>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default BulkPaymentLinkCheckout;
